import { isTauri } from '../../utils/tauri';
import { parseBitwardenJson, parseBitwardenCsv, parseLastpassCsv, parseApibooJson } from '../../utils/vaultPortability';

/**
 * Import from other password managers (Bitwarden JSON+CSV, LastPass CSV) plus
 * our own Apiboo exports. Pure desktop-vault feature — no bridge. Opened from
 * the vault titlebar's download/upload actions via the 'vault-import' /
 * 'vault-export' window CustomEvents (see VaultView).
 *
 * EXPORT (M2 of plans/vault-improvement-plan-2026-08-08.md). The file content
 * is built HOST-SIDE by `vault_export_data` — the old vault_list +
 * vault_get_item assembly loop is gone, because it decrypted the entire vault
 * into JS heap strings nothing can zeroize. Two more gates the plan demands:
 *
 *   - EVERY export re-authenticates through `vault_export_authorize` first, so
 *     an unlocked, unattended app is not two clicks from a full credential
 *     dump — and the host refuses the export without the ticket it returns;
 *   - "Encrypted" (its own file password, min 8 chars, may differ from the
 *     master passphrase) is the DEFAULT; plaintext requires ticking an explicit
 *     acknowledgement before the button arms.
 *
 * The Bitwarden/LastPass CSV *export* options are still here — removing them
 * was proposed when the JS assembly loop went, and reviewed OUT: they are a
 * user-facing migration feature, and this repo's rule is that removal is an
 * instruction, never an inference from adjacent work. They are plaintext by
 * nature, so they sit behind the SAME re-auth gate and the SAME plaintext
 * acknowledgement, and their items come from the host (`vault_export_data`
 * plaintext) — the JS assembly loop stays gone; only the CSV serialization of
 * the host's answer happens here.
 *
 * IMPORT is unchanged for plain files. A file whose JSON carries
 * format:"apiboo-vault-export" is an encrypted export: it is unsealed
 * host-side (`vault_export_unseal`) with its own file password, and the
 * resulting items feed the same per-item path as every other source.
 *
 * Parsing/serialization for plain formats lives in ../utils/vaultPortability
 * (pure, tested); this component only wires file I/O (Tauri dialog or browser
 * <input>) and the vault_* commands around it.
 */

export const IMPORT_SOURCES = {
  bitwardenJson: { parse: parseBitwardenJson, ext: ['json'], labelKey: 'vaultIO.sourceBitwardenJson' },
  bitwardenCsv: { parse: parseBitwardenCsv, ext: ['csv'], labelKey: 'vaultIO.sourceBitwardenCsv' },
  lastpassCsv: { parse: parseLastpassCsv, ext: ['csv'], labelKey: 'vaultIO.sourceLastpassCsv' },
  apibooJson: { parse: parseApibooJson, ext: ['json'], labelKey: 'vaultIO.sourceApibooJson' },
};

export const IMPORT_ORDER = ['bitwardenJson', 'bitwardenCsv', 'lastpassCsv', 'apibooJson'];

/** Top-level `format` value the Rust exporter stamps on an encrypted envelope. */
export const SEALED_EXPORT_MARKER = 'apiboo-vault-export';

/**
 * Is `text` an encrypted vault export? Returns the (BOM-stripped) content to
 * hand to `vault_export_unseal`, or null for anything else.
 *
 * The substring pre-check is not decoration: a large plain JSON import
 * (Bitwarden exports run to megabytes) would otherwise be fully parsed a
 * second time just to learn it has no marker. False positives are fine — the
 * marker string inside, say, an item name still has to survive the real
 * `format` check on the parsed object.
 */
export function sealedExportContent(text) {
  const s = String(text == null ? '' : text);
  const raw = s.charCodeAt(0) === 0xfeff ? s.slice(1) : s;
  if (!raw.includes(SEALED_EXPORT_MARKER)) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed.format === SEALED_EXPORT_MARKER) return raw;
  } catch {
    /* not JSON → not an envelope */
  }
  return null;
}

/**
 * The wire vocabulary `ExportError::to_token` emits (vault_export.rs) → the
 * locale key that explains it. Rust deliberately sends a stable token and
 * keeps the detail in its own log (§4.3), which makes this map the ONLY place
 * the user-facing wording can live — without it the modal rendered the token
 * itself, so a German user read the literal string `export_password_wrong`
 * and the seven `vaultIO.exportErr*` translations were never loaded by
 * anything.
 *
 * Rename a token in Rust and this map must move with it; the pairing is
 * asserted in vaultExportEncryption.test.js.
 */
export const EXPORT_ERROR_KEYS = {
  export_password_required: ['exportErrPasswordRequired', 'Choose a password for the encrypted export.'],
  export_password_too_short: ['exportErrPasswordTooShort', 'The file password must be at least 8 characters.'],
  export_password_too_long: ['exportErrPasswordTooLong', 'That file password is too long.'],
  export_password_wrong: ['exportErrPasswordWrong', 'Wrong password for this file, or the file is damaged.'],
  export_unsupported: ['exportErrUnsupported', 'This file is not a vault export this version can open.'],
  export_corrupt: ['exportErrCorrupt', 'This export file is damaged.'],
  export_failed: ['exportErrFailed', 'Export failed — nothing was written.'],
  export_format_unknown: ['exportErrFailed', 'Export failed — nothing was written.'],
  export_reauth_required: ['exportErrReauth', 'Confirm your master passphrase again, then export.'],
  export_reauth_expired: ['exportErrReauth', 'Confirm your master passphrase again, then export.'],
};

/**
 * A rejection from `vault_export_data` / `vault_export_unseal` → the sentence
 * to show. Tauri rejects with the plain error string, but a thrown JS Error
 * can arrive here too, so both shapes are unwrapped before the lookup.
 *
 * Anything unrecognised falls through to its own text rather than a generic
 * "something went wrong": an unmapped token is a bug in this map, and hiding
 * it behind friendly copy is how it stays a bug.
 */
export function exportErrorMessage(t, e, fallbackKey, fallbackText) {
  const raw = String((e && e.message) || e || '').trim();
  const known = EXPORT_ERROR_KEYS[raw];
  if (known) return t(`vaultIO.${known[0]}`, known[1]);
  if (fallbackKey) return t(`vaultIO.${fallbackKey}`, fallbackText);
  return raw;
}

/** Dedupe key for an existing vault envelope (matches util's dedupeKey shape). */
export function existingEnvelopeKey(env) {
  const ix = env.index || {};
  const name = String(env.name || '').trim().toLowerCase();
  const username = String(ix.username || '').trim().toLowerCase();
  const host = String(ix.urlHost || '').toLowerCase();
  return `${name}\u0000${username}\u0000${host}`;
}

/** Native save dialog (Tauri) or anchor-download (browser). */
export async function saveTextFile(content, filename, ext, mime) {
  if (isTauri()) {
    const { save } = await import('@tauri-apps/plugin-dialog');
    const { writeTextFile } = await import('@tauri-apps/plugin-fs');
    const filters =
      ext === 'json'
        ? [{ name: 'JSON', extensions: ['json'] }]
        : [{ name: 'CSV', extensions: ['csv'] }];
    const filePath = await save({ defaultPath: filename, title: 'Export vault', filters });
    if (filePath == null) return { saved: false, cancelled: true };
    await writeTextFile(filePath, content);
    return { saved: true, path: filePath };
  }
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  return { saved: true };
}
